import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import {
  getAdminList,
  getRoleList,
  AdminUser,
  Role,
  CreateAdminPayload,
  createAdmin,
  UpdateAdminPayload,
  updateAdmin,
  ResetAdminPasswordPayload,
  resetAdminPassword,
} from "../../apis/accessControlApis";

/* ================= STATE ================= */

interface AccessControlState {
  admins: AdminUser[];
  roles: Role[];
  listLoading: boolean; // table loading
  roleLoading: boolean; // role dropdown loading
  actionLoading: boolean; // create/edit/reset loading
  error: string | null;
}

const initialState: AccessControlState = {
  admins: [],
  roles: [],
  listLoading: false,
  roleLoading: false,
  actionLoading: false,
  error: null,
};

/* ================= THUNKS ================= */

export const fetchAdminListThunk = createAsyncThunk<
  AdminUser[],
  void,
  { rejectValue: string }
>("accessControl/fetchAdminList", async (_, { rejectWithValue }) => {
  try {
    const response = await getAdminList();
    if (!response.status) {
      return rejectWithValue("Failed to fetch admin list");
    }
    return response.data;
  } catch (error: any) {
    return rejectWithValue(
      error?.response?.data?.message || "Failed to fetch admin list",
    );
  }
});

export const fetchRoleListThunk = createAsyncThunk<
  Role[],
  void,
  { rejectValue: string }
>("accessControl/fetchRoleList", async (_, { rejectWithValue }) => {
  try {
    const response = await getRoleList();
    if (!response.status) {
      return rejectWithValue("Failed to fetch role list");
    }
    return response.data;
  } catch (error: any) {
    return rejectWithValue(
      error?.response?.data?.message || "Failed to fetch role list",
    );
  }
});

export const createAdminThunk = createAsyncThunk<
  AdminUser,
  CreateAdminPayload,
  { rejectValue: string }
>("accessControl/createAdmin", async (payload, { rejectWithValue }) => {
  try {
    const response = await createAdmin(payload);
    if (!response.status) {
      return rejectWithValue("Failed to create admin");
    }
    return response.data;
  } catch (error: any) {
    return rejectWithValue(
      error?.response?.data?.data || "Failed to create admin",
    );
  }
});

export const updateAdminThunk = createAsyncThunk<
  UpdateAdminPayload,
  UpdateAdminPayload,
  { rejectValue: string }
>("accessControl/updateAdmin", async (payload, { rejectWithValue }) => {
  try {
    const response = await updateAdmin(payload);
    if (!response.status) {
      return rejectWithValue("Failed to update admin");
    }
    return payload;
  } catch (error: any) {
    return rejectWithValue(
      error?.response?.data?.data || "Failed to update admin",
    );
  }
});

export const resetAdminPasswordThunk = createAsyncThunk<
  string,
  ResetAdminPasswordPayload,
  { rejectValue: string }
>("accessControl/resetAdminPassword", async (payload, { rejectWithValue }) => {
  try {
    const response = await resetAdminPassword(payload);
    if (!response.status) {
      return rejectWithValue("Failed to reset password");
    }
    return response.data;
  } catch (error: any) {
    return rejectWithValue(
      error?.response?.data?.data || "Failed to reset password",
    );
  }
});

/* ================= SLICE ================= */

const accessControlSlice = createSlice({
  name: "accessControl",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    const getErrorMessage = (payload: unknown, fallback: string) =>
      typeof payload === "string" ? payload : fallback;

    builder

      /* ---------- FETCH ADMIN LIST ---------- */
      .addCase(fetchAdminListThunk.pending, (state) => {
        state.listLoading = true;
      })
      .addCase(fetchAdminListThunk.fulfilled, (state, action) => {
        state.listLoading = false;
        state.admins = action.payload;
      })
      .addCase(fetchAdminListThunk.rejected, (state, action) => {
        state.listLoading = false;
        state.error = getErrorMessage(
          action.payload,
          "Failed to fetch admin list",
        );
        toast.error(state.error);
      })

      /* ---------- FETCH ROLE LIST ---------- */
      .addCase(fetchRoleListThunk.pending, (state) => {
        state.roleLoading = true;
      })
      .addCase(fetchRoleListThunk.fulfilled, (state, action) => {
        state.roleLoading = false;
        state.roles = action.payload;
      })
      .addCase(fetchRoleListThunk.rejected, (state, action) => {
        state.roleLoading = false;
        state.error = getErrorMessage(
          action.payload,
          "Failed to fetch role list",
        );
        toast.error(state.error);
      })

      /* ---------- CREATE ADMIN ---------- */
      .addCase(createAdminThunk.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(createAdminThunk.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.admins.unshift(action.payload);
        toast.success("Admin created successfully");
      })
      .addCase(createAdminThunk.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = getErrorMessage(action.payload, "Failed to create admin");

        toast.error(action.payload);
      })

      /* ---------- UPDATE ADMIN ---------- */
      .addCase(updateAdminThunk.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(updateAdminThunk.fulfilled, (state, action) => {
        state.actionLoading = false;

        const index = state.admins.findIndex(
          (admin) => admin.id === action.payload.id,
        );

        if (index !== -1) {
          state.admins[index] = {
            ...state.admins[index],
            ...action.payload,
            role:
              state.roles.find((r) => r.id === action.payload.roleId) ||
              state.admins[index].role,
            updatedAt: new Date().toISOString(),
          };
        }

        if (action.payload.isActive === false) {
          toast.success("Access deleted successfully");
        } else {
          toast.success("Access updated successfully");
        }
      })
      .addCase(updateAdminThunk.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = getErrorMessage(action.payload, "Failed to update admin");
        toast.error(state.error);
      })

      /* ---------- RESET PASSWORD ---------- */
      .addCase(resetAdminPasswordThunk.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(resetAdminPasswordThunk.fulfilled, (state, action) => {
        state.actionLoading = false;
        toast.success("Password reset successfully");
      })
      .addCase(resetAdminPasswordThunk.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = getErrorMessage(
          action.payload,
          "Failed to reset password",
        );
        toast.error(state.error);
      });
  },
});

export default accessControlSlice.reducer;
